'use client';

import { 
  Stack, 
  Box, 
  Button, 
  Text, 
  Group, 
  Title, 
  TextInput, 
  PasswordInput, 
  Alert, 
  Divider, 
  Center, 
  Anchor,
  Checkbox 
} from '@mantine/core';
import { IconAlertTriangle, IconUser } from '@tabler/icons-react';
import LocationAutocomplete from '@/components/ui/LocationAutocomplete';
import { Turnstile } from '@marsidev/react-turnstile';

interface AuthForm {
  name: string;
  email: string;
  password: string;
  location: string;
  linkedinUrl?: string;
  agreedToTerms?: boolean;
}

interface AuthStepProps {
  authMode: 'signin' | 'signup';
  onAuthModeChange: (mode: 'signin' | 'signup') => void;
  onSubmit: (values: AuthForm) => void;
  authError: string;
  authLoading: boolean;
  formValues: AuthForm;
  formErrors: Partial<AuthForm>;
  onFieldChange: (field: keyof AuthForm, value: string) => void;
  mentorId: string;
  turnstileToken: string;
  onTurnstileChange: (token: string) => void;
}

export default function AuthStep({
  authMode,
  onAuthModeChange,
  onSubmit,
  authError,
  authLoading,
  formValues,
  formErrors,
  onFieldChange,
  mentorId,
  turnstileToken,
  onTurnstileChange
}: AuthStepProps) {
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formValues);
  };

  return (
    <Stack gap="lg">
      <Text c="dimmed" mb="sm">
        {authMode === 'signup' 
          ? 'Create an account to continue with your application'
          : 'Sign in to your existing account'
        }
      </Text>

      {authError && (
        <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
          {authError}
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Stack gap="md">
          {authMode === 'signup' && (
            <>
              <TextInput
                label="Full Name"
                placeholder="Your full name"
                value={formValues.name}
                onChange={(e) => onFieldChange('name', e.currentTarget.value)}
                error={formErrors.name}
                required
                size="md"
              />
              <LocationAutocomplete
                label="Location"
                placeholder="Search for your city or location..."
                value={formValues.location}
                onChange={(value) => onFieldChange('location', value)}
                error={formErrors.location}
                required
              />
            </>
          )}
          
          <TextInput
            label="Email"
            placeholder="your.email@example.com"
            type="email"
            value={formValues.email}
            onChange={(e) => onFieldChange('email', e.currentTarget.value)}
            error={formErrors.email}
            required={authMode === 'signup'}
            size="md"
          />
          
          <PasswordInput
            label="Password"
            placeholder={authMode === 'signup' ? 'Create a secure password' : 'Enter your password'}
            value={formValues.password}
            onChange={(e) => onFieldChange('password', e.currentTarget.value)}
            error={formErrors.password}
            required={authMode === 'signup'}
            size="md"
          />
          
          {authMode === 'signup' && (
            <>
              <Checkbox
                label={
                  <Text size="sm">
                    I agree to the{' '}
                    <Anchor href="/terms" target="_blank" rel="noopener noreferrer" size="sm">
                      Terms of Service
                    </Anchor>
                    {' '}and{' '}
                    <Anchor href="/privacy" target="_blank" rel="noopener noreferrer" size="sm">
                      Privacy Policy
                    </Anchor>
                  </Text>
                }
                checked={formValues.agreedToTerms || false}
                onChange={(e) => onFieldChange('agreedToTerms' as keyof AuthForm, e.currentTarget.checked as any)}
                error={formErrors.agreedToTerms}
              />
              <Turnstile
                siteKey={process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || '1x00000000000000000000AA'}
                onSuccess={(token) => onTurnstileChange(token)}
                onError={() => {}}
              />
            </>
          )}
          
          <Button 
            type="submit" 
            loading={authLoading} 
            size="md"
            fullWidth
          >
            {authMode === 'signup' ? 'Create Account' : 'Sign In'}
          </Button>
        </Stack>
      </form>

      <Divider label="or" labelPosition="center" />

      <Center>
        <Anchor
          onClick={() => {
            onAuthModeChange(authMode === 'signup' ? 'signin' : 'signup');
          }}
          size="sm"
        >
          {authMode === 'signup' 
            ? 'Already have an account? Sign in' 
            : "Don't have an account? Sign up"
          }
        </Anchor>
      </Center>
    </Stack>
  );
}
